import { Controller } from "@hotwired/stimulus";

// Live filters: the filter form sends itself while the user types or picks
// a value. It adds to the manual search, it does not replace it: the
// "Filter" button and the "Reset" link keep working as before.
//
//   - text field  → sent after a short pause in typing (debounce);
//   - select, date, checkbox, radio → sent at once; a multi-select once its
//     list closes;
//   - a text of a single character is not sent (too broad, too costly),
//     a number is;
//   - nothing is sent when the form holds the same values as the last send.
//
// When the form targets a Turbo frame, only the results are replaced and the
// field keeps its focus. When it does not, Turbo replaces the whole page: the
// focused field, its cursor and the scroll position are put back on connect.
//
// A field opts out with data-live-filters-ignore (e.g. an export option).
//
// The form is not re-rendered by a search, so a Reset link drawn hidden (no
// filter when the page loaded) is a "reset" target: shown while a filter
// holds a value, hidden when every filter is empty.

// Survives the page replacement of a non-frame visit.
let pendingRestore = null;

const TEXT_TYPES = ["text", "search", "email", "tel", "url", "number"];

export default class extends Controller {
  static targets = ["reset"];

  static values = {
    delay: { type: Number, default: 400 },
    minLength: { type: Number, default: 2 },
  };

  connect() {
    this.timeout = null;
    this.lastSent = this.serialize();
    this.onSubmit = this.onSubmit.bind(this);
    this.element.addEventListener("submit", this.onSubmit);
    this.restore();
    this.toggleReset();
  }

  disconnect() {
    clearTimeout(this.timeout);
    this.element.removeEventListener("submit", this.onSubmit);
  }

  // data-action="input->live-filters#queue change->live-filters#queue"
  queue(event) {
    const field = event.target;
    if (!this.watches(field)) return;

    if (this.isText(field)) {
      if (event.type !== "input") return;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.send(field), this.delayValue);
      return;
    }

    // SlimSelect and flatpickr fire both "input" and "change": keep one.
    if (event.type !== "change") return;
    clearTimeout(this.timeout);
    this.send(field);
  }

  send(field) {
    // A one-digit number (a threshold, a number of days) is a full value.
    if (this.isText(field) && field.type !== "number") {
      const length = field.value.trim().length;
      if (length > 0 && length < this.minLengthValue) return;
    }
    if (this.serialize() === this.lastSent) return;

    this.remember(field);
    this.element.requestSubmit();
  }

  // Any send, live or manual (button, Enter), becomes the new reference.
  onSubmit() {
    clearTimeout(this.timeout);
    this.lastSent = this.serialize();
    this.toggleReset();
  }

  toggleReset() {
    if (!this.hasResetTarget) return;
    this.resetTarget.style.display = this.hasActiveFilter() ? "" : "none";
  }

  // A visible field holding a value: hidden fields only carry context.
  hasActiveFilter() {
    return Array.from(this.element.elements).some((field) => {
      if (!field.name || field.disabled || field.type === "hidden") return false;
      if (["submit", "button", "reset"].includes(field.type)) return false;
      if (field.type === "checkbox" || field.type === "radio") return field.checked;
      return field.value.trim() !== "";
    });
  }

  watches(field) {
    // A multi-select (shared/ds/multiselect) carries its name on its root and
    // tells its change when its list closes.
    const name = field?.name || field?.dataset?.dsMultiselectNameValue;
    if (!field || !name || field.disabled) return false;
    if (field.closest("[data-live-filters-ignore]")) return false;
    return field.form === this.element || this.element.contains(field);
  }

  isText(field) {
    // A date picker (flatpickr) is a read-only text input: it is a pick.
    if (field.readOnly || field.classList.contains("flatpickr-input")) return false;
    return field.tagName === "TEXTAREA" ||
      (field.tagName === "INPUT" && TEXT_TYPES.includes(field.type));
  }

  serialize() {
    return new URLSearchParams(new FormData(this.element)).toString();
  }

  // Only a whole-page visit replaces the form; a frame visit leaves it alone.
  remember(field) {
    if (this.targetsFrame()) return;
    pendingRestore = {
      key: this.key(),
      name: field.name,
      cursor: this.isText(field) ? field.selectionStart : null,
      scrollY: window.scrollY,
    };
    // The user may keep typing while the page loads: take the field as it is
    // just before the new page replaces it, not as it was sent.
    const restore = pendingRestore;
    document.addEventListener("turbo:before-render", () => {
      if (pendingRestore !== restore) return;
      restore.value = field.value;
      if (restore.cursor !== null) restore.cursor = field.selectionStart;
    }, { once: true });
  }

  restore() {
    if (!pendingRestore || pendingRestore.key !== this.key()) return;
    const { name, cursor, scrollY, value } = pendingRestore;
    pendingRestore = null;

    // Turbo scrolls to the top once the new page is rendered: scroll back after.
    window.scrollTo(0, scrollY);
    document.addEventListener("turbo:load", () => window.scrollTo(0, scrollY), { once: true });
    const field = this.element.querySelector(`[name="${CSS.escape(name)}"]`);
    if (!field || !this.isText(field)) return;
    // Typed after the send: put it back and send it in turn.
    if (value !== undefined && field.value !== value) {
      field.value = value;
      this.timeout = setTimeout(() => this.send(field), this.delayValue);
    }
    field.focus({ preventScroll: true });
    if (cursor !== null) {
      const position = Math.min(cursor, field.value.length);
      field.setSelectionRange(position, position);
    }
  }

  targetsFrame() {
    const frame = this.element.dataset.turboFrame;
    return Boolean(frame && frame !== "_top") || Boolean(this.element.closest("turbo-frame:not([target='_top'])"));
  }

  key() {
    return this.element.id || this.element.getAttribute("action");
  }
}
